import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { authService } from '../../services/authService';
import { AuthState, LoginResponse, MagicLinkRequest, MagicLinkVerify, User } from '../../types';
import { loadJson, saveJson } from '../../utils/storage';
import { storageKeys } from '../../config';

const buildUser = (response: LoginResponse): User => ({
  id: Number(response.userId),
  email: response.userEmail,
  name: response.userAlias.replace(/\+/g, ' '),
  token: response.accessToken,
  refreshToken: response.refreshToken,
  role: response.userRole,
  hrDashboardAdmin: response.hrDashboardAdmin,
  hrDashboardFiller: response.hrDashboardFiller,
});

const storedUser = loadJson<User | null>(storageKeys.user, null);

const initialState: AuthState = {
  user: storedUser ? { ...storedUser, id: Number(storedUser.id) } : null,
  isAuthenticated: Boolean(storedUser),
  isLoading: false,
  verifyingToken: false,
  magicLinkSent: false,
  error: null,
};

export const requestMagicLink = createAsyncThunk(
  'auth/requestMagicLink',
  async (payload: MagicLinkRequest, { rejectWithValue }) => {
    try {
      await authService.requestMagicLink(payload);
      return true;
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Failed to send magic link');
    }
  }
);

export const verifyMagicLink = createAsyncThunk(
  'auth/verifyMagicLink',
  async (payload: MagicLinkVerify, { rejectWithValue }) => {
    try {
      const response = await authService.verifyMagicLink(payload);
      const user = buildUser(response);
      saveJson(storageKeys.user, user);
      return user;
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Invalid or expired link');
    }
  }
);

export const logout = createAsyncThunk('auth/logout', async () => {
  if (typeof window !== 'undefined') {
    window.localStorage.removeItem(storageKeys.user);
  }
});

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    resetMagicLinkSent: (state) => {
      state.magicLinkSent = false;
    },
    bootstrapUser: (state, action: PayloadAction<User | null>) => {
      state.user = action.payload ? { ...action.payload, id: Number(action.payload.id) } : null;
      state.isAuthenticated = Boolean(action.payload);
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(requestMagicLink.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(requestMagicLink.fulfilled, (state) => {
        state.isLoading = false;
        state.magicLinkSent = true;
      })
      .addCase(requestMagicLink.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      .addCase(verifyMagicLink.pending, (state) => {
        state.verifyingToken = true;
        state.error = null;
      })
      .addCase(verifyMagicLink.fulfilled, (state, action: PayloadAction<User>) => {
        state.verifyingToken = false;
        state.user = action.payload;
        state.isAuthenticated = true;
        state.magicLinkSent = false;
      })
      .addCase(verifyMagicLink.rejected, (state, action) => {
        state.verifyingToken = false;
        state.error = action.payload as string;
      })
      .addCase(logout.fulfilled, (state) => {
        state.user = null;
        state.isAuthenticated = false;
        state.magicLinkSent = false;
      });
  },
});

export const { clearError, resetMagicLinkSent, bootstrapUser } = authSlice.actions;
export default authSlice.reducer;